import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './Context/AuthContext';
import { Spa, Category, Business } from '@mui/icons-material';
import './App.css';
import NavBar from './Components/NavBar';
import GlobalLoader from './Components/GlobalLoader';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const DashboardHome = React.lazy(() => import('./Components/Home'));
const Reports = React.lazy(() => import('./Components/Reports'));
const IndividualForm = React.lazy(() => import('./Components/IndividualForm'));
const IndividualList = React.lazy(() => import("./Components/IndividualList"));
const IndividualDataAnalysis = React.lazy(() => import("./Components/IndividualDataAnalysis"));
const IndividualEditForm = React.lazy(() => import("./Components/IndividualEditForm"));
const AddEnterpriseForm = React.lazy(() => import("./Components/EnterpriseForm"));
const EnterpriseList = React.lazy(() => import("./Components/EnterpriseList"));
const EnterpriseEditForm = React.lazy(() => import("./Components/EnterpriseEditForm"));
const SettingsBeneficiaries = React.lazy(() => import("./Components/SettingsBeneficiaries"));
const SettingsEvents = React.lazy(() => import("./Components/SettingsEvents"));
const SettingsProjects = React.lazy(() => import("./Components/SettingsProjects"));
const SettingsUsers = React.lazy(() => import("./Components/SettingsUsers"));
const UserContributionsDashboard = React.lazy(() => import('./Components/SettingsContributions'));
const LogIn = React.lazy(() => import("./Components/LogIn"));
const ForgotPassword = React.lazy(() => import("./Components/ForgotPassword"));
const VerifyCode = React.lazy(() => import("./Components/VerifyCode"));
const ResetPassword = React.lazy(() => import("./Components/ResetPassword"));

const ProjectForm = React.lazy(() => import("./Components/ProjectForm"));
const ProjectList = React.lazy(() => import("./Components/ProjectList"));
const ProjectEditForm = React.lazy(() => import("./Components/ProjectEditForm"));
const ProjectDetail = React.lazy(() => import("./Components/ProjectDetail"));
const EventForm = React.lazy(() => import("./Components/EventForm"));
const EventList = React.lazy(() => import("./Components/EventsList"));
const EventEditForm = React.lazy(() => import("./Components/EventEditForm"));
const EventDetail = React.lazy(() => import("./Components/EventDetail"));
const DonorManagement = React.lazy(() => import("./Components/SettingsForm"));
const EntityManagement = React.lazy(() => import("./Components/SettingsEventTypeForm"));
const ObjectManagement = React.lazy(() => import("./Components/SpeciesForm"));
const BaseManagement = React.lazy(() => import('./Components/SpeciesForm'));
const EntityTypeManagement = React.lazy(() => import("./Components/TypeFormSettings"));
const FUGReport = React.lazy(() => import("./Components/ReportingFUG"));
const IndividualReport = React.lazy(() => import("./Components/ReportingIndividual"));
const EnterpriseReport = React.lazy(() => import("./Components/ReportingEnterprise"));
const DistrictReport = React.lazy(() => import("./Components/DistrictReport"));
const ProvienceEntities = React.lazy(() => import("./Components/ProvienceRecord"));
const SubActivityTargetForm = React.lazy(() => import("./Components/subActivityTargetForm"));
const ActivityOutputLinking = React.lazy(() => import("./Components/ActivityOutputLinking"));
const EnterpriseDetail = React.lazy(() => import("./Components/EnterpriseDetail"));
const GroupsDetail = React.lazy(() => import("./Components/GroupsDetail"));
const SelectIndividuals = React.lazy(() => import("./Components/SelectBeneficiary"));
const SelectEnterprise = React.lazy(() => import("./Components/SelectEnterprise"));
const SelectGroups = React.lazy(() => import("./Components/SelectGroups"));
const ActivityForm = React.lazy(() => import("./Components/ActivityForm"));
const OutcomesForm = React.lazy(() => import("./Components/OutcomesForm"));
const OutputsForm = React.lazy(() => import("./Components/OutputsForm"));
const Notifications = React.lazy(() => import("./Components/Notifications"));
const LrpForm = React.lazy(() => import("./Components/LrpForm"));
const LrpDetail = React.lazy(() => import("./Components/LrpDetail"));
const LrpList = React.lazy(() => import("./Components/LrpList"));

const AddInventoryForm = React.lazy(() => import("./Components/InventoryForm"));
const InventoryPlanningForm =  React.lazy(() => import("./Components/InventoryPlanningForm"));
const InventoryPlanningDetail =  React.lazy(() => import("./Components/InventoryPlanningDetail"));
const InventoryPlanningList =  React.lazy(() => import("./Components/InventoryPlanningList"));
const InventoryCompletionPlotForm = React.lazy(() => import("./Components/InventoryCompletionPlotForm"));
const InventoryPrecisionForm = React.lazy(() => import("./Components/InventoryPrecisionForm"));
const InventoryPlotForm = React.lazy(() => import("./Components/InventoryPlotForm"));
const InventoryPlotList = React.lazy(() => import("./Components/InventoryPlotList"));
const InventoryPlotDetail = React.lazy(() => import("./Components/InventoryPlotDetail"));
const InventoryList = React.lazy(() => import("./Components/InventoryList"));
const InventoryEditForm = React.lazy(() => import("./Components/InventoryEditForm"));
const InventoryTeamForm = React.lazy(() => import("./Components/InventoryTeamForm"));
const InventoryTeamList = React.lazy(() => import("./Components/InventoryTeamList"));

const InventoryDetail = React.lazy(() => import("./Components/InventoryDetail"));
const PlotImageForm = React.lazy(() => import("./Components/InventoryPlotImage"));
const InventoryReport = React.lazy(() => import("./Components/ReportingInventory"));
const InventoryReportingForm = React.lazy(() => import("./Components/InventoryReportingForm"));
const InventoryStoryList = React.lazy(() => import("./Components/InventoryStoryList"));
const InventoryStoryDetail = React.lazy(() => import("./Components/InventoryStoryDetail"));
const InventoryStoryForm = React.lazy(() => import("./Components/InventoryStoryForm"));
const ActivityLogsPage = React.lazy(() => import("./Components/ActivityLogsPage"));
const MyProfile = React.lazy(() => import("./Components/MyProfile"));


const FUGForm = React.lazy(() => import("./Components/FUGForm"));
const FUGList = React.lazy(() => import('./Components/FUGList'));
const EditFUGForm = React.lazy(() => import('./Components/FUGEditForm'));
const GeographicalCharts = React.lazy(() => import('./Components/GeographicalCharts'));
const SDMDashboard = React.lazy(() => import('./Components/SDMDashboard'));


const HallBookingCalendar = React.lazy(() => import('./Components/HallBookingList'));
const FinanceForm = React.lazy(() => import('./Components/FinanceForm'));
const PlanningFullForm = React.lazy(() => import('./Components/PlanningForm'));
const PlanningList = React.lazy(() => import('./Components/PlanningList'));
const PlanningDetail = React.lazy(() => import('./Components/PlanningDetail'));
const YearlyTargetForm = React.lazy(() => import('./Components/YearlyTargetForm'));
const YearlyTargetList = React.lazy(() => import('./Components/YearlyTargetList'));

const API_BASE = process.env.REACT_APP_API_URL;

function App() {
  // Global fix: Prevent numeric field value changes on scroll wheel
  React.useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  return (
      <AuthProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Suspense fallback={<GlobalLoader />}>
            <Routes>
        <Route path="/" element={<NavBar />}>
          <Route index element={<DashboardHome />} />
          <Route path="/projects/:id/geographical" element={<GeographicalCharts />} />
           <Route path="reports" element={<Reports />} />
            <Route path="add-project" element={<ProjectForm />} />
            <Route path="projects" element={<ProjectList />} />
            <Route path="/projects/edit/:id" element={<ProjectEditForm />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/projects/:id/event-add" element={<EventForm />} />
            <Route path="/projects/:id/events" element={<EventList />} />

            <Route path="/individuals/:id/events" element={<EventList />} />

            <Route path="/projects/:id/inventory-add" element={<AddInventoryForm />} />
            <Route path="/projects/:id/inventory" element={<InventoryList />} />
            <Route path="/inventory/edit/:id" element={<InventoryEditForm />} />
            <Route path="/inventory/:id" element={<InventoryDetail />} />

            <Route path="/inventory-add" element={<AddInventoryForm />} />
            <Route path="/inventory-plot-add" element={<InventoryPlotForm />} />
            <Route path="/inventory-plots/edit/:id" element={<InventoryPlotForm />} />
            <Route path="/inventory-plan" element={<InventoryPlanningForm />} />
            <Route path="/inventory-supervisor-reporter" element={<InventoryReportingForm />} />
            <Route path="/inventory-planning/:id" element={<InventoryPlanningDetail />} />
            <Route path="/inventory-planning/edit/:id" element={<InventoryPlanningForm />} />
            <Route path="/inventory-plan-list" element={<InventoryPlanningList />} />
            <Route path="/inventory-plots" element={<InventoryPlotList />} />
            <Route path="/inventory-plots/:id" element={<InventoryPlotDetail />} />
            <Route path="inventory" element={<InventoryList />} />
            <Route path="inventory/report" element={<InventoryReport />} />
            <Route path="/plot-images/new" element={<PlotImageForm />} />
            <Route path="/plot-images/edit/:id" element={<PlotImageForm />} />

            <Route path="/inventory/team/new" element={<InventoryTeamForm />} />
            <Route path="/inventory-team/edit/:id" element={<InventoryTeamForm />} />
            <Route path="/inventory/teamlist" element={<InventoryTeamList />} />


            <Route path="inventory-completion-plot/new" element={<InventoryCompletionPlotForm />} />
            <Route path="inventory-completion-plot/edit/:id" element={<InventoryCompletionPlotForm />} />
            <Route path="inventory-precision/new" element={<InventoryPrecisionForm />} />
            <Route path="inventory-precision/edit/:id" element={<InventoryPrecisionForm />} />

            {/* Inventory Stories */}
            <Route path="/inventory-stories" element={<InventoryStoryList />} />
            <Route path="/inventory-stories/new" element={<InventoryStoryForm />} />
            <Route path="/inventory-stories/:id" element={<InventoryStoryDetail />} />
            <Route path="/inventory-stories/edit/:id" element={<InventoryStoryForm />} />



                  <Route path="/lrp-list" element={<LrpList />} />
      <Route path="/lrp/new" element={<LrpForm />} />
      <Route path="/lrp/:id" element={<LrpDetail />} />
      <Route path="/lrp/edit/:id" element={<LrpForm />} />


            <Route path="/events/edit/:id" element={<EventEditForm />} />
            <Route path="/events/view/:id" element={<EventDetail />} />
            <Route path="add-individual" element={<IndividualForm />} />
            <Route path="/events/:id/add-individual" element={<IndividualForm />} />
            <Route path="/events/:id/select-individuals" element={<SelectIndividuals />} />
            <Route path="/events/:id/add-enterprise" element={<AddEnterpriseForm />} />
            <Route path="/events/:id/select-enterprises" element={<SelectEnterprise />} />



            <Route path="/events/:id/fug" element={<FUGForm />} />
            <Route path="/individuals/edit/:id" element={<IndividualEditForm />} />
            <Route path="individuals" element={<IndividualList />} />
            <Route path="individuals/report" element={<IndividualReport />} />
            <Route path="individuals/analysis" element={<IndividualDataAnalysis />} />
            <Route path="/events/:id/individuals" element={<IndividualList />} />
            <Route path="fug" element={<FUGForm />} />
            <Route path="fuglist" element={<FUGList />} />
            <Route path="/events/:id/fuglist" element={<FUGList />} />
            <Route path="/events/:id/select-fugs" element={<SelectGroups />} />
            <Route path="fuglist/report" element={<FUGReport />} />
            <Route path="/groups/:id/edit" element={<EditFUGForm />} />
            <Route path="/group/:id" element={<GroupsDetail />} />
            <Route path="add-enterprise" element={<AddEnterpriseForm />} />
            <Route path="enterprise" element={<EnterpriseList />} />
            <Route path="enterprise/:id" element={<EnterpriseDetail />} />
            <Route path="/events/:id/enterprise" element={<EnterpriseList />} />
            <Route path="enterprise/report" element={<EnterpriseReport />} />
            <Route path="/enterprise/edit/:id" element={<EnterpriseEditForm />} />
            <Route path="settings/beneficiaries" element={<SettingsBeneficiaries />} />
            <Route path="settings/events" element={<SettingsEvents />} />
            <Route path="settings/project" element={<SettingsProjects />} />
            <Route path="/user-contributions" element={<UserContributionsDashboard />} />
            <Route path="users" element={<SettingsUsers />} />
            <Route path="settings/project/donor" element={<DonorManagement entityName="Donor" apiEndpoint={`${API_BASE}/api/donors/`} fieldName="donor"/>}/>
            <Route path="settings/project/program_area" element={<DonorManagement entityName="Program Aream" apiEndpoint={`${API_BASE}/api/programarea/`} fieldName="program_area"/>}/>
            <Route path="settings/project/partners" element={<DonorManagement entityName="Partner" apiEndpoint={`${API_BASE}/api/partners/`} fieldName="partners"/>}/>
            <Route path="settings/project/event_unit" element={<DonorManagement entityName="Event's Unit" apiEndpoint={`${API_BASE}/api/unitevents/`} fieldName="unit_events"/>}/>
            <Route path="settings/project/beneficiary_unit" element={<DonorManagement entityName="Beneficiary's Unit" apiEndpoint={`${API_BASE}/api/unitbeneficiary/`} fieldName="unit_beneficiary"/>}/>
            <Route path="settings/project/stakeholders" element={<DonorManagement entityName="Stakeholder" apiEndpoint={`${API_BASE}/api/stakeholders/`} fieldName="stakeholder"/>}/>
            <Route path="settings/project/beneficiaries" element={<DonorManagement entityName="Beneficiary" apiEndpoint={`${API_BASE}/api/beneficiary/`} fieldName="beneficiary"/>}/>
            <Route path="settings/event/event-level" element={<DonorManagement entityName="Event Level" apiEndpoint={`${API_BASE}/api/event_level/`} fieldName="Event_Level"/>}/>
            <Route
              path="settings/event/event-type"
              element={
                <EntityManagement
                  entityName="Event Type"
                  apiEndpoint={`${API_BASE}/api/event_type/`}
                  fieldName="Event_Type"
                />
              }
            />
            <Route path="settings/beneficiaries/education" element={<DonorManagement entityName="Education" apiEndpoint={`${API_BASE}/api/education/`} fieldName="education"/>}/>
            <Route
  path="/settings/beneficiaries/beneficiary-types"
  element={
    <DonorManagement
      entityName="Beneficiary Type"
      apiEndpoint={`${API_BASE}/api/beneficiarytype/`}
      fieldName="beneficiary_types"
    />
  }
/>
                   <Route path="settings/beneficiaries/pwd" element={<DonorManagement entityName="PWD" apiEndpoint={`${API_BASE}/api/pwd/`} fieldName="pwd_types"/>}/>
            <Route path="settings/beneficiaries/ethnicity" element={<DonorManagement entityName="Ethinicity" apiEndpoint={`${API_BASE}/api/ethnicities/`} fieldName="ethinicity"/>}/>
            <Route path="settings/beneficiaries/economic-status" element={<DonorManagement entityName="Economic Status" apiEndpoint={`${API_BASE}/api/economic/`} fieldName="economic_status"/>}/>
            <Route
  path="settings/beneficiaries/positions"
  element={
    <ObjectManagement
      entityName="Positions"
      apiEndpoint={`${API_BASE}/api/positions/`}
      fieldName="Position_Name"
      commodityEndpoint={`${API_BASE}/api/organization/`}
      // Position-specific configuration
      nameField="Position_Name"
      descriptionField="description"
      parentField="organization"
      parentLabel="Organization"
      parentIcon={Business}
      entityIcon={Business}
      nameLabel="Position Name"
      descriptionLabel="Position Description"
      nameRequired={true}
    />
  }
/>


            <Route path="settings/beneficiaries/commodity" element={<DonorManagement entityName="Commodity" apiEndpoint={`${API_BASE}/api/commodity/`} fieldName="commodity"/>}/>
            <Route path="settings/beneficiaries/group-type" element={<DonorManagement entityName="Group Type" apiEndpoint={`${API_BASE}/api/grouptype/`} fieldName="Group"/>}/>
            <Route path="settings/beneficiaries/group" element={<DonorManagement entityName="Group Status" apiEndpoint={`${API_BASE}/api/group/`} fieldName="Group_Status"/>}/>
            <Route path="settings/beneficiaries/registered" element={<DonorManagement entityName="Registered" apiEndpoint={`${API_BASE}/api/registered/`} fieldName="registered"/>}/>
            <Route
  path="settings/beneficiaries/ntfp-species"
  element={
    <ObjectManagement
      entityName="Species"
      apiEndpoint={`${API_BASE}/api/species/`}
      fieldName="species"
      commodityEndpoint={`${API_BASE}/api/commodity/`}
      // Species-specific configuration
      nameField="species"
      descriptionField="description"
      parentField="commodity"
      parentLabel="Commodity"
      parentIcon={Category}
      entityIcon={Spa}
      nameLabel="Species Name"
      descriptionLabel="Description"
      nameRequired={true}
    />
  }
/>


            <Route path="settings/beneficiaries/ownership-type" element={<DonorManagement entityName="Ownership Type" apiEndpoint={`${API_BASE}/api/ownership/`} fieldName="Ownership"/>}/>
            <Route path="settings/beneficiaries/enterprise-size" element={<DonorManagement entityName="Enterprise Size" apiEndpoint={`${API_BASE}/api/enterprise_size/`} fieldName="Enterprise_Size"/>}/>
            <Route path="settings/beneficiaries/technologies" element={<DonorManagement entityName="Technology" apiEndpoint={`${API_BASE}/api/technology/`} fieldName="Technology"/>}/>
            <Route path="settings/beneficiaries/products" element={<DonorManagement entityName="Products" apiEndpoint={`${API_BASE}/api/products/`} fieldName="products"/>}/>
            <Route path="settings/beneficiaries/resource" element={<DonorManagement entityName="Resource Base" apiEndpoint={`${API_BASE}/api/resource-base/`} fieldName="Resource_Base"/>}/>
            <Route path="settings/beneficiaries/function" element={<DonorManagement entityName="Function Base" apiEndpoint={`${API_BASE}/api/function-base/`} fieldName="Function_Base"/>}/>
            <Route path="settings/beneficiaries/organization" element={
              <EntityTypeManagement
                entityName="Organization"
                apiEndpoint={`${API_BASE}/api/organization/`}
                fieldName="organization_name"
              />
            }/>

            <Route path="settings/beneficiaries/forest-classification" element={
              <EntityTypeManagement
                entityName="Forest"
                apiEndpoint={`${API_BASE}/api/forest/`}
                fieldName="Forest_Name"
              />
            }/>

            <Route path="settings/beneficiaries/enterprise-type" element={
              <EntityTypeManagement
                entityName="Enterprise"
                apiEndpoint={`${API_BASE}/api/enterprisetype/`}
                fieldName="Enterprise_Type"
               />
            }/>
                    <Route path="settings/beneficiaries/certified_status" element={
              <EntityTypeManagement
                entityName="Certified Status"
                apiEndpoint={`${API_BASE}/api/certified_status/`}
                fieldName="certified_status"
              />
            }/>
            <Route path="settings/beneficiaries/forest-category" element={
              <EntityTypeManagement
                entityName="Frest Category"
                apiEndpoint={`${API_BASE}/api/forestcategory/`}
                fieldName="Forest_Category"
              />
            }/>

            <Route path="settings/beneficiaries/forest-regime" element={
              <DonorManagement
                entityName="Forest Regime"
                apiEndpoint={`${API_BASE}/api/forestregime/`}
                fieldName="forest_regime"
              />
            }/>

            <Route path="settings/beneficiaries/observations" element={
              <DonorManagement
                entityName="Observations"
                apiEndpoint={`${API_BASE}/api/observations/`}
                fieldName="destruction"
              />
            }/>

            <Route path="settings/beneficiaries/soil-texture" element={
              <DonorManagement
                entityName="Soil Texture"
                apiEndpoint={`${API_BASE}/api/soil_texture/`}
                fieldName="soil_texture"
              />
            }/>


            <Route path="settings/beneficiaries/object-name" element={<ObjectManagement />} />
            <Route path="settings/beneficiaries/aspect" element={
              <DonorManagement
                entityName="Aspect"
                apiEndpoint={`${API_BASE}/api/aspects/`}
                fieldName="aspect"
              />
            }/>
            <Route path="settings/beneficiaries/market" element={
              <EntityTypeManagement
                entityName="Market"
                apiEndpoint={`${API_BASE}/api/market/`}
                fieldName="market"
              />
            }/>

            <Route path="district_report" element={<DistrictReport />} />
            <Route path="provience_record" element={<ProvienceEntities />} />
          <Route path="projects/:id/activities/:activityId/sub-activities/add" element={<SubActivityTargetForm />} />
            <Route
              path="/projects/:projectId/activities/:activityId/sub-activities/:subActivityId/edit"
              element={<SubActivityTargetForm />}
            />
            // Add this route to your App.js
<Route path="/projects/:projectId/outputs/:outputId/link-activities" element={<ActivityOutputLinking />} />
            <Route path="projects/:projectId/activities/add" element={<ActivityForm />} />
            <Route path="projects/:projectId/activities/:id/edit" element={<ActivityForm />} />

            <Route path="projects/:projectId/outcomes/add" element={<OutcomesForm />} />
            <Route path="projects/:projectId/outcomes/:id/edit" element={<OutcomesForm />} />

            <Route path="projects/:projectId/outputs/add" element={<OutputsForm />} />
            <Route path="projects/:projectId/outputs/:id/edit" element={<OutputsForm />} />

            <Route path="notifications" element={<Notifications />} />
            <Route path="/activity-logs" element={<ActivityLogsPage />} />

            <Route path="/hall-booking" element={<HallBookingCalendar / >} />
<Route path="/finance/new" element={<FinanceForm />} />
<Route path="/finance/edit/:id" element={<FinanceForm />} />

<Route path="/planning/list" element={<PlanningList />} />
<Route path="/planning/:id" element={<PlanningDetail />} />
<Route path="/planning/new" element={<PlanningFullForm />} />
<Route path="/planning/:id/edit" element={<PlanningFullForm />} />
<Route path="/yearly-targets/add" element={<YearlyTargetForm />} />
<Route path="/yearly-targets/edit/:id" element={<YearlyTargetForm />} />
<Route path="/yearly-targets/list" element={<YearlyTargetList />} />
<Route path="/yearly-targets" element={<YearlyTargetList />} />


            <Route path="/profile" element={<MyProfile />} />

            {/* SDM — Species Distribution Modelling */}
            <Route path="/sdm" element={<SDMDashboard />} />





        </Route>
                    <Route path="login" element={<LogIn />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/verify-code" element={<VerifyCode />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
            </Routes>
          </Suspense>
        </LocalizationProvider>
      </AuthProvider>
  );
}

export default App;